import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Avatar,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TeamMemberService from '../../services/teamMemberService';
import RoleService from '../../services/roleService';
import api from '../../services/api';
import { useAppData } from '../../context/AppDataContext';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { getServiceName } from '../../utils/serviceTranslations';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import { getRoleName } from '../../utils/roleTranslations';
import { getPermissionName, groupPermissionsByCategory, orderedPermissionCategoryEntries } from '../../utils/permissionTranslations';
import { formatDurationForDisplay } from '../../utils/durationUtils';
import { formatCurrency, DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const TeamMemberViewPage = () => {
  const { t, i18n } = useTranslation();
  const { storeId, teamMemberId } = useParams();
  const navigate = useNavigate();
  const { currentStore } = useAppData();

  const [teamMember, setTeamMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [storeServices, setStoreServices] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [permissionsTranslations, setPermissionsTranslations] = useState({});

  const currencyCode = currentStore?.currency || DEFAULT_CURRENCY;

  const loadTeamMember = async () => {
    if (!storeId) {
      setError(t('teamMembers.error.noStoreSelected'));
      setLoading(false);
      return;
    }
    const requestKey = `getTeamMember-${teamMemberId}-${storeId}`;
    return requestDeduplicator.deduplicate(requestKey, async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await TeamMemberService.getTeamMember(teamMemberId, storeId);
        setTeamMember(data);
      } catch (err) {
        setError(t('teamMembers.error.loading'));
        console.error('Error loading team member:', err);
      } finally {
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    loadTeamMember();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamMemberId, storeId]);

  // Load services available in the current store to resolve default price/duration/name
  useEffect(() => {
    const loadServices = async () => {
      if (!storeId) return;
      const requestKey = `getServices-${storeId}`;
      return requestDeduplicator.deduplicate(requestKey, async () => {
        try {
          const list = await TeamMemberService.getServicesByStore(storeId);
          setStoreServices(list || []);
        } catch (e) {
          // Silent fail, we will just not show defaults if unavailable
          console.warn('Failed to load store services', e);
        }
      });
    };
    loadServices();
  }, [storeId]);

  // Load role permissions and permission translations
  useEffect(() => {
    const loadRoleAndPermissions = async () => {
      if (!teamMember?.store?.roleId || !storeId) {
        setRolePermissions([]);
        return;
      }

      try {
        // Load role details to get permissions
        const rolesKey = `getRoles-${storeId}`;
        const rolesData = await requestDeduplicator.deduplicate(rolesKey, async () => {
          return await RoleService.getRolesByStore();
        });

        const role = rolesData?.find(r => r.id === teamMember.store.roleId);
        if (role) {
          if (Array.isArray(role.permissions)) {
            setRolePermissions(role.permissions);
          } else if (typeof role.permissions === 'object' && role.permissions !== null) {
            setRolePermissions(Object.keys(role.permissions));
          }
        }

        // Load permissions translations
        const permissionsKey = 'getAllPermissions';
        const permissionsData = await requestDeduplicator.deduplicate(permissionsKey, async () => {
          const response = await api.get('/permissions');
          return response.data;
        });

        setPermissionsTranslations(permissionsData || {});
      } catch (error) {
        console.error('Failed to load role or permissions:', error);
      }
    };

    loadRoleAndPermissions();
  }, [teamMember?.store?.roleId, storeId]);

  const getPermissionLabel = (permission) => {
    if (permissionsTranslations[permission]) {
      return getPermissionName(permissionsTranslations[permission], i18n.language);
    }
    return permission;
  };

  if (loading) {
    return <ManagementPageSkeleton variant="detail" showLeadingAvatar />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={loadTeamMember} variant="outlined">
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  if (!teamMember) {
    return null;
  }

  return (
    <ManagementPageLayout
      pageTitle={teamMember.name}
      pageSubtitle={t('teamMembers.actions.view')}
      leadingContent={(
        <Avatar
          src={teamMember.picture ?? teamMember.Picture}
          sx={{
            width: 80,
            height: 80,
            mx: 'auto',
            mb: 2,
            bgcolor: brandAlpha('accent', 0.2),
            border: `2px solid ${brandAlpha('accent', 0.5)}`,
          }}
        >
          <PersonIcon sx={{ fontSize: 40, color: BRAND.accentText }} />
        </Avatar>
      )}
    >
      <ManagementPageCard>
          <Box sx={{ p: 3, borderBottom: `1px solid ${BRAND.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/management/stores/${storeId}/team-members`)} variant="outlined" sx={{ borderColor: BRAND.accent, color: BRAND.accentText }}>
              {t('common.back')}
            </Button>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title={t('teamMembers.actions.edit')}>
                <IconButton component={Link} to={`/management/stores/${storeId}/team-members/${teamMemberId}/edit`} sx={{ color: '#1976d2' }}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Box sx={{ p: 4 }}>
            <Box sx={{ border: `1px solid ${BRAND.line}`, borderRadius: 2, p: 3, backgroundColor: '#f8f9fa' }}>
              {/* Basic Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600, mb: 2 }}>
                  {t('teamMembers.registration.personalInfo')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">{t('teamMembers.registration.email')}:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{teamMember.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">{t('teamMembers.registration.phone')}:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{teamMember.phone ? `${teamMember.phone.countryCode}${teamMember.phone.number}` : t('common.notProvided')}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">{t('teamMembers.registration.genre')}:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{teamMember.genre ? t(`teamMembers.registration.${teamMember.genre.toString().toLowerCase()}`) : t('common.notProvided')}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">{t('teamMembers.registration.birthDate')}:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{teamMember.birthDate ? new Date(teamMember.birthDate).toLocaleDateString('pt-BR') : t('common.notProvided')}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">{t('teamMembers.registration.language')}:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{t(`common.languages.${teamMember.language?.toString().toLowerCase()}`)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">{t('teamMembers.registration.jobTitle')}:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {teamMember.store?.jobTitle || t('common.notProvided')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">{t('teamMembers.acceptsCustomerBookings.label')}:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {teamMember.store?.acceptsCustomerBookings !== false ? t('common.yes') : t('common.no')}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Role & Permissions */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600, mb: 2 }}>
                  {t('teamMembers.registration.steps.step2.title')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">{t('teamMembers.registration.role')}:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {teamMember.store?.role 
                        ? getRoleName(teamMember.store.role, i18n.language)
                        : t('common.notProvided')}
                    </Typography>
                  </Grid>
                </Grid>
                {(rolePermissions.length > 0 || (teamMember.store?.additionalPermissions && teamMember.store.additionalPermissions.length > 0)) && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('teamMembers.registration.permissions')}:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {rolePermissions.length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            {t('teamMembers.registration.fromRole')}:
                          </Typography>
                          {orderedPermissionCategoryEntries(groupPermissionsByCategory(rolePermissions)).map(([category, perms]) => (
                            <Box key={category} sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 0.5 }}>
                                {t(`roles.modal.form.categories.${category}`)}:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {perms.map(perm => (
                                  <Chip
                                    key={perm}
                                    label={getPermissionLabel(perm)}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#e3f2fd',
                                      color: '#1976d2',
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                      {teamMember.store?.additionalPermissions && teamMember.store.additionalPermissions.length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            {t('teamMembers.registration.additional')}:
                          </Typography>
                          {orderedPermissionCategoryEntries(groupPermissionsByCategory(teamMember.store.additionalPermissions)).map(([category, perms]) => (
                            <Box key={category} sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 0.5 }}>
                                {t(`roles.modal.form.categories.${category}`)}:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {perms.map(perm => (
                                  <Chip
                                    key={perm}
                                    label={getPermissionLabel(perm)}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#e8f5e9',
                                      color: '#2e7d32',
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Services */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600, mb: 2 }}>
                  {t('teamMembers.registration.services')}
                </Typography>
                {(!teamMember.services || teamMember.services.length === 0) ? (
                  <Typography variant="body2" color="text.secondary">{t('teamMembers.registration.noServicesSelected')}</Typography>
                ) : (
                  <Grid container spacing={2}>
                    {teamMember.services.map((svc, idx) => {
                      const meta = storeServices.find(s => s.id === svc.serviceId) || {};
                      const name = meta.name ? getServiceName(meta.name, i18n.language) : t('common.notProvided');
                      const priceValue = (svc.customPrice != null ? svc.customPrice : meta.price != null ? meta.price : 0);
                      const durationDisplay = formatDurationForDisplay(meta.duration);
                      return (
                        <Grid item xs={12} sm={6} md={4} key={svc.serviceId || idx}>
                          <Box sx={{ p: 2, border: `1px solid ${BRAND.line}`, borderRadius: 1, backgroundColor: '#fff' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                              {name}
                            </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>{t('teamMembers.registration.price')}:</strong> {formatCurrency(Number(priceValue) || 0, currencyCode)}
                            {svc.customPrice != null && (
                              <span style={{ color: BRAND.accentText, marginLeft: '4px' }}>
                                ({t('teamMembers.registration.custom')})
                              </span>
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>{t('teamMembers.registration.duration')}:</strong> {durationDisplay || '00h00'}
                          </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            </Box>
          </Box>
        </ManagementPageCard>
    </ManagementPageLayout>
  );
};

export default TeamMemberViewPage;


